import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  RotateCcw,
  CheckCircle2,
  XCircle,
  Clock,
  Receipt,
  Eye,
} from "lucide-react";
import { useRefunds, useUpdateRefundStatus } from "../api";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from "@/components/ui";
import { PageLoader, PageError } from "@/components/common";
import { formatCurrency, formatDateTime, cn } from "@/lib/utils";
import { usePermissions } from "@/stores";
import type { Refund, RefundStatus } from "@/types";

const statusConfig: Record<
  RefundStatus,
  { label: string; icon: React.ElementType; color: string; bg: string }
> = {
  pending: {
    label: "Pending",
    icon: Clock,
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
  },
  approved: {
    label: "Approved",
    icon: CheckCircle2,
    color: "text-green-700",
    bg: "bg-green-50 border-green-200",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    color: "text-red-700",
    bg: "bg-red-50 border-red-200",
  },
};

const VALID_TABS: RefundStatus[] = ["pending", "approved", "rejected"];

export function RefundsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: RefundStatus = VALID_TABS.includes(tabParam as RefundStatus)
    ? (tabParam as RefundStatus)
    : "pending";

  const handleTabChange = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const [openRefund, setOpenRefund] = useState<Refund | null>(null);

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="border-b bg-background">
        <div className="container mx-auto px-4 py-4 md:px-6">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <RotateCcw className="h-6 w-6" />
            Refunds
          </h1>
          <p className="text-sm text-muted-foreground">
            Review and approve refund requests
          </p>
        </div>
      </div>
      <div className="container mx-auto px-4 py-4 md:px-6">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList className="mb-4 w-full justify-start bg-background md:w-auto">
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="h-4 w-4" />
              Pending
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="h-4 w-4" />
              Rejected
            </TabsTrigger>
          </TabsList>
          <TabsContent value={tab} className="mt-0">
            <RefundList status={tab} onView={setOpenRefund} />
          </TabsContent>
        </Tabs>
      </div>
      {openRefund && (
        <RefundDetailsDialog
          refund={openRefund}
          onClose={() => setOpenRefund(null)}
        />
      )}
    </div>
  );
}

interface RefundListProps {
  status: RefundStatus;
  onView: (refund: Refund) => void;
}

function RefundList({ status, onView }: RefundListProps) {
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission("refunds.approve");

  const { data, isLoading, error, refetch } = useRefunds({ status, limit: 50 });
  const updateStatus = useUpdateRefundStatus();
  const navigate = useNavigate();

  if (isLoading) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load refunds" onRetry={refetch} />;

  const refunds = data?.data ?? [];

  if (refunds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed bg-background py-16">
        <RotateCcw className="mb-4 h-12 w-12 text-muted-foreground/50" />
        <h3 className="text-lg font-medium">No {status} refunds</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {status === "pending"
            ? "New refund requests will appear here"
            : `Refunds with status “${status}” will appear here`}
        </p>
      </div>
    );
  }

  const handleDecision = async (
    refund: Refund,
    decision: "approved" | "rejected",
  ) => {
    try {
      await updateStatus.mutateAsync({ id: refund.id, status: decision });
      toast.success(
        decision === "approved" ? "Refund approved" : "Refund rejected",
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update refund",
      );
    }
  };

  return (
    <div className="space-y-2">
      {refunds.map((refund) => {
        const config = statusConfig[refund.status];

        return (
          <Card key={refund.id} className="transition-colors hover:bg-muted/40">
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
              <div
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                  config.bg,
                )}
              >
                <config.icon className={cn("h-5 w-5", config.color)} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-bold">#{refund.order.orderNumber}</span>
                  <Badge
                    variant="outline"
                    className={cn("text-xs", config.color)}
                  >
                    {config.label}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    {refund.items.length} item
                    {refund.items.length === 1 ? "" : "s"}
                  </Badge>
                </div>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {refund.reason}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(refund.createdAt)} · by{" "}
                  {refund.processedBy.firstName} {refund.processedBy.lastName}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold">
                  {formatCurrency(refund.amount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  of {formatCurrency(refund.order.total)}
                </p>
              </div>
              <div
                className="flex shrink-0 flex-wrap items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onView(refund)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/orders/${refund.order.id}/invoice`)}
                >
                  <Receipt className="h-4 w-4" />
                </Button>
                {refund.status === "pending" && canApprove && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => handleDecision(refund, "rejected")}
                      disabled={updateStatus.isPending}
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-700 hover:bg-green-800"
                      onClick={() => handleDecision(refund, "approved")}
                      disabled={updateStatus.isPending}
                    >
                      Approve
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

interface RefundDetailsDialogProps {
  refund: Refund;
  onClose: () => void;
}

function RefundDetailsDialog({ refund, onClose }: RefundDetailsDialogProps) {
  const config = statusConfig[refund.status];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Refund · #{refund.order.orderNumber}
          </DialogTitle>
          <DialogDescription>
            <Badge className={cn(config.bg, config.color, "border")}>
              <config.icon className="mr-1 h-3 w-3" />
              {config.label}
            </Badge>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-muted/50 p-3 text-sm">
            <p className="text-xs text-muted-foreground">Reason</p>
            <p>{refund.reason}</p>
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold text-muted-foreground">
              Refunded items
            </h4>
            <div className="space-y-1 rounded-md border p-3 text-sm">
              {refund.items.map((line) => (
                <div key={line.id} className="flex justify-between">
                  <span>{line.quantity}× item</span>
                  <span className="font-medium">
                    {formatCurrency(line.amount)}
                  </span>
                </div>
              ))}
              <Separator className="my-2" />
              <div className="flex justify-between font-semibold">
                <span>Total refunded</span>
                <span>{formatCurrency(refund.amount)}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Requested by</p>
              <p className="font-medium">
                {refund.processedBy.firstName} {refund.processedBy.lastName}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="font-medium">{formatDateTime(refund.createdAt)}</p>
            </div>
            {refund.approvedBy && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">Decided by</p>
                <p className="font-medium">
                  {refund.approvedBy.firstName} {refund.approvedBy.lastName}
                </p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
